import { useCallback, useMemo } from "react"

import { isEqual } from "lodash-es"

import { generateUUID } from "shared/lib/generateUUID"
import { useEvent } from "shared/lib/useEvent"
import { ProductPosition } from "shared/types/ProductPosition"
import { isProjectProductPosition } from "shared/types/Project"

import {
  FAVORITES_PROJECT_ID,
  useFavoriteMutations
} from "entities/favorites/api/useFavoriteMutations"
import { useProjectData } from "entities/projects/api/useProjectData"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { ProductConfiguratorResult } from "widgets/product-configurator/model/useProductConfiguratorModel"

export function useFavorites() {
  const {
    project,
    isLoading: isLoadingChildren,
    error
  } = useProjectData(FAVORITES_PROJECT_ID, true)
  const { createFavorite, updatePosition, updateFavorite, deleteFavorite } =
    useFavoriteMutations()

  const favorites = useMemo(
    () => (project?.children as ProductPosition[]) || [],
    [project?.children]
  )

  const getFavorite = useCallback(
    (configuration: ProductConfiguratorResult) =>
      favorites?.find(productPosition => {
        if (isProjectProductPosition(productPosition)) {
          let favorite = true
          if (configuration.variantKey !== productPosition.variantKey) {
            favorite = false
          }
          if (
            productPosition.configurationKey &&
            configuration.configurationKey !== productPosition.configurationKey
          ) {
            favorite = false
          }
          if (
            productPosition.dimensioning &&
            !isEqual(
              productPosition.dimensioning,
              configuration.externalDimensioningResult
            )
          ) {
            favorite = false
          }
          return favorite
        }
        return false
      }),
    [favorites]
  )

  const isFavorite = useCallback(
    (configuration: ProductConfiguratorResult) => !!getFavorite(configuration),
    [favorites]
  )

  const productTotal = useMemo(() => getProductTotal(favorites), [favorites])

  const toggleFavorite = useEvent(
    (configuration: ProductConfiguratorResult) => {
      const favorite = getFavorite(configuration)
      if (favorite) {
        return deleteFavorite({
          id: favorite.id
        })
      }
      return createFavorite({
        projectID: FAVORITES_PROJECT_ID,
        productPosition: {
          id: generateUUID(),
          quantity: 1,
          variantKey: configuration.variantKey,
          configurationKey: configuration.configurationKey,
          dimensioning: configuration.externalDimensioningResult
        }
      })
    }
  )

  const addToFavorites = useEvent(
    (configuration: ProductConfiguratorResult) => {
      if (isFavorite(configuration)) {
        return Promise.resolve()
      }
      return createFavorite({
        projectID: FAVORITES_PROJECT_ID,
        productPosition: {
          id: generateUUID(),
          quantity: 1,
          variantKey: configuration.variantKey,
          configurationKey: configuration.configurationKey,
          dimensioning: configuration.externalDimensioningResult
        }
      })
    }
  )

  const duplicateFavorite = useEvent((id: string) => {
    const productPosition = favorites?.find(variant => id === variant.id)
    if (productPosition) {
      return createFavorite({
        projectID: FAVORITES_PROJECT_ID,
        productPosition
      })
    }
    return Promise.resolve()
  })

  const removeFromFavorites = useEvent(
    (configuration: ProductConfiguratorResult) => {
      const favorite = getFavorite(configuration)
      if (!favorite) {
        return Promise.resolve()
      }
      return deleteFavorite({
        id: favorite?.id
      })
    }
  )

  return {
    favorites,
    isFavorite,
    isLoading: isLoadingChildren,
    productTotal,
    error,
    toggleFavorite,
    addToFavorites,
    updateFavorite,
    updatePosition,
    duplicateFavorite,
    removeFromFavorites
  }
}
